import RequireAuth from 'components/RequireAuth';
import { AuthPortalProvider } from 'contexts/AuthPortalProvider';
import { useConfig } from 'contexts/ConfigProvider';
import { LoadingMaskProvider } from 'contexts/LoadingMaskProvider';
import { ModalProvider } from 'contexts/ModalProvider';
import { t } from 'i18next';
import Loading from 'pages/Loading';
import Rtty from 'pages/Rtty';
import { Suspense, lazy } from 'react';
import { RouteObject, useLocation, useRoutes } from 'react-router-dom';
import { BuildVersionList } from 'utils/helper';
import { getItem, removeItem, setItem } from 'utils/localStorageHelper';
import { AuthProvider } from './contexts/AuthProvider';
import { ToastProvider } from './contexts/ToastProvider';
import { PortalRole } from './pages/PortalProfile';
import { Role } from './pages/Profile';
export interface RouteObjectWithBreadcrumbs extends RouteObject {
  children?: RouteObjectWithBreadcrumbs[];
  breadcrumb?: string;
}

/* portal */
const PortalLogin = lazy(() => import('./pages/PortalLogin'));
const PortalSite = lazy(() => import('./pages/PortalSite'));
const PortalMember = lazy(() => import('./pages/PortalMember'));
const PortalProfile = lazy(() => import('./pages/PortalProfile'));
const PortalLayout = lazy(() => import('./portalLayout'));
const PortalDevices = lazy(() => import('./pages/PortalDevices'));

/* contoller */
const Layout = lazy(() => import('./layout'));
const Profile = lazy(() => import('./pages/Profile'));
const Blank = lazy(() => import('./pages/Blank'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ClientDevices = lazy(() => import('./pages/devices/ClientDevices'));
const EapDevices = lazy(() => import('./pages/devices/EAPDevices'));
const EapDevice = lazy(() => import('./pages/devices/EAPDevices/EapDevice'));
const SwitchDevice = lazy(() => import('./pages/devices/SwitchDevices/SwitchDevice'));
const SwitchDevices = lazy(() => import('./pages/devices/SwitchDevices'));
const Network = lazy(() => import('./pages/settings/Network'));
const CreateEditNetwork = lazy(() => import('./pages/settings/Network/CreateEditNetwork'));
const Wifi = lazy(() => import('./pages/settings/Wifi'));
const Internet = lazy(() => import('./pages/settings/Internet'));
const EditInternet = lazy(() => import('./pages/settings/Internet/EditInternet'));
const ProfileManagement = lazy(() => import('./pages/settings/ProfileManagement'));
const CreateEditRadio = lazy(() => import('./pages/settings/ProfileManagement/Radios/CreateEditRadio'));
const CreateEditRadius = lazy(() => import('./pages/settings/ProfileManagement/Radius/CreateEditRadius'));
const CreateEditWirelessNetwork = lazy(() => import('./pages/settings/ProfileManagement/WirelessNetwork/CreateEditWirelessNetwork'));
const CreateEditVirtualNetwork = lazy(() => import('./pages/settings/ProfileManagement/VirtualNetwork/CreateEditVirtualNetwork'));
const CreateEditCaptivePortal = lazy(() => import('./pages/settings/ProfileManagement/CaptivePortal/CreateEditCaptivePortal'));
const System = lazy(() => import('./pages/settings/System'));
const RedirectLanding = lazy(() => import('./pages/RedirectLanding'));
const FirmwareUpdate = lazy(() => import('./pages/settings/FirmwareUpdate'));
const ReleaseNote = lazy(() => import('./pages/settings/FirmwareUpdate/ReleaseNote'));
const Statistics = lazy(() => import('./pages/Statistics'));
const WifiScanner = lazy(() => import('./pages/insights/WifiScanner'));
const DhcpLeases = lazy(() => import('./pages/insights/DhcpLeases'));
const StaticRouteFirewall = lazy(() => import('./pages/settings/StaticRouteFirewall'));
const CreateEditPortForward = lazy(() => import('./pages/settings/StaticRouteFirewall/CreateEditPortForward'));
const CreateEditStaticRoute = lazy(() => import('./pages/settings/StaticRouteFirewall/CreateEditStaticRoute'));
const CreateEditFirewall = lazy(() => import('./pages/settings/StaticRouteFirewall/CreateEditFirewall'));
const Topology = lazy(() => import('./pages/Topology'));
const ControllerPortSettings = lazy(() => import('./pages/Topology/ControllerPortSettings'));
const MailServicesConfig = lazy(() => import('./pages/settings/System/MailServicesConfig'));
const SystemLog = lazy(() => import('./pages/settings/System/SystemLog'));
const NoMatch = lazy(() => import('./pages/NoMatch'));

export const getRoutes = (buildVersion: string, role?: string | null): RouteObjectWithBreadcrumbs[] => {
  const addSiteIdPrefix = (routes: RouteObjectWithBreadcrumbs[], prefix = '/sites/:siteId') =>
    routes.map((route) => {
      const updatedRoute = { ...route };
      if (updatedRoute.path && updatedRoute.path !== '*') {
        updatedRoute.path = `${prefix}${updatedRoute.path}`;
      }
      if (updatedRoute.children) {
        updatedRoute.children = addSiteIdPrefix(updatedRoute.children, prefix);
      }
      return updatedRoute;
    });

  const controllerRoute: RouteObjectWithBreadcrumbs[] = [
    {
      path: '/',
      breadcrumb: 'Home',
      element: (
        <Suspense fallback={<Loading />}>
          <RequireAuth>
            <Layout />
          </RequireAuth>
        </Suspense>
      ),
      children: [
        { index: true, element: <Dashboard /> },
        {
          path: '/dashboard',
          element: (
            <Suspense fallback={<Loading />}>
              <Dashboard />
            </Suspense>
          ),
        },
        {
          path: '/topology',
          breadcrumb: t('topology.pageTitle'),
          element: <Blank />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <Topology />
                </Suspense>
              ),
            },
            {
              path: '/topology/controller-port-settings/:serialNumber',
              breadcrumb: ':serialNumber',
              element: <Blank />,
              children: [
                {
                  index: true,
                  element: (
                    <Suspense fallback={<Loading />}>
                      <ControllerPortSettings tab={0} />
                    </Suspense>
                  ),
                },
                {
                  path: '/topology/controller-port-settings/:serialNumber/controller-port-management',
                  element: (
                    <Suspense fallback={<Loading />}>
                      <ControllerPortSettings tab={0} />
                    </Suspense>
                  ),
                },
                {
                  path: '/topology/controller-port-settings/:serialNumber/controller-advanced-settings',
                  element: (
                    <Suspense fallback={<Loading />}>
                      <ControllerPortSettings tab={1} />
                    </Suspense>
                  ),
                },
              ],
            },
          ],
        },
        {
          path: '/devices',
          element: <Blank />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <EapDevices />
                </Suspense>
              ),
            },
            {
              path: '/devices/eap-devices',
              breadcrumb: t('devices.eapDevices.pageTitle'),
              children: [
                {
                  index: true,
                  element: (
                    <Suspense fallback={<Loading />}>
                      <EapDevices />
                    </Suspense>
                  ),
                },
                {
                  path: '/devices/eap-devices/:serialNumber',
                  breadcrumb: ':serialNumber',
                  element: <Blank />,
                  children: [
                    {
                      index: true,
                      element: (
                        <Suspense fallback={<Loading />}>
                          <EapDevice tab={0} />
                        </Suspense>
                      ),
                    },
                    {
                      path: '/devices/eap-devices/:serialNumber/eap-port-management',
                      element: (
                        <Suspense fallback={<Loading />}>
                          <EapDevice tab={0} />
                        </Suspense>
                      ),
                    },
                    {
                      path: '/devices/eap-devices/:serialNumber/eap-advanced-settings',
                      element: (
                        <Suspense fallback={<Loading />}>
                          <EapDevice tab={1} />
                        </Suspense>
                      ),
                    },
                  ],
                },
              ],
            },
            {
              path: '/devices/client-devices',
              breadcrumb: t('devices.clientDevices.pageTitle'),
              element: (
                <Suspense fallback={<Loading />}>
                  <ClientDevices />
                </Suspense>
              ),
            },
            {
              path: '/devices/switch-devices',
              breadcrumb: t('devices.switchDevices.pageTitle'),
              children: [
                {
                  index: true,
                  element: (
                    <Suspense fallback={<Loading />}>
                      <SwitchDevices />
                    </Suspense>
                  ),
                },
                {
                  path: '/devices/switch-devices/:serialNumber',
                  breadcrumb: ':serialNumber',
                  element: <Blank />,
                  children: [
                    {
                      index: true,
                      element: (
                        <Suspense fallback={<Loading />}>
                          <SwitchDevice tab={0} />
                        </Suspense>
                      ),
                    },
                    {
                      path: '/devices/switch-devices/:serialNumber/switch-port-management',
                      element: (
                        <Suspense fallback={<Loading />}>
                          <SwitchDevice tab={0} />
                        </Suspense>
                      ),
                    },
                    {
                      path: '/devices/switch-devices/:serialNumber/switch-advanced-settings',
                      element: (
                        <Suspense fallback={<Loading />}>
                          <SwitchDevice tab={1} />
                        </Suspense>
                      ),
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: '/statistics',
          breadcrumb: t('statistics.pageTitle'),
          element: <Suspense fallback={<Loading />}>{buildVersion === BuildVersionList.controller ? <Statistics /> : <NoMatch />}</Suspense>,
        },
        {
          path: '/insights',
          element: <Blank />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <WifiScanner />
                </Suspense>
              ),
            },
            {
              index: true,
              path: '/insights/wifi-scan',
              breadcrumb: t('insights.wifiScanner.pageTitle'),
              element: (
                <Suspense fallback={<Loading />}>
                  <WifiScanner />
                </Suspense>
              ),
            },
            {
              index: true,
              path: '/insights/dhcp-leases',
              breadcrumb: t('insights.dhcpLeases.pageTitle'),
              element: <Suspense fallback={<Loading />}>{buildVersion === BuildVersionList.controller ? <DhcpLeases /> : <NoMatch />}</Suspense>,
            },
          ],
        },
        {
          path: '/settings',
          breadcrumb: t('sidenav.navsSettings'),
          element: <Blank />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <Wifi />
                </Suspense>
              ),
            },
            {
              path: '/settings/wifi',
              breadcrumb: t('settings.wifi.pageTitle'),
              element: <Blank />,
              children: [
                {
                  index: true,
                  element: (
                    <Suspense fallback={<Loading />}>
                      <Wifi />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: '/settings/network',
              breadcrumb: t('settings.network.pageTitle'),
              element: <Blank />,
              children: [
                {
                  index: true,
                  element: <Suspense fallback={<Loading />}>{buildVersion === BuildVersionList.controller ? <Network /> : <NoMatch />}</Suspense>,
                },
                {
                  path: '/settings/network/:name',
                  breadcrumb: ':name',
                  element: (
                    <Suspense fallback={<Loading />}>{buildVersion === BuildVersionList.controller ? <CreateEditNetwork /> : <NoMatch />}</Suspense>
                  ),
                },
              ],
            },
            {
              path: '/settings/internet',
              breadcrumb: t('settings.internet.pageTitle'),
              element: <Blank />,
              children: [
                {
                  index: true,
                  element: <Suspense fallback={<Loading />}>{buildVersion === BuildVersionList.controller ? <Internet /> : <NoMatch />}</Suspense>,
                },
                {
                  path: '/settings/internet/:name',
                  breadcrumb: ':name',
                  element: (
                    <Suspense fallback={<Loading />}>{buildVersion === BuildVersionList.controller ? <EditInternet /> : <NoMatch />}</Suspense>
                  ),
                },
              ],
            },
            {
              path: '/settings/profileManagement',
              breadcrumb: t('sidenav.navsProfileManage'),
              element: <Blank />,
              children: [
                {
                  path: '',
                  element: (
                    <Suspense fallback={<Loading />}>
                      <ProfileManagement tab={0} />
                    </Suspense>
                  ),
                },
                {
                  path: '/settings/profileManagement/wirelessNetwork',
                  breadcrumb: t('settings.profileManagement.ssidsPageTitle'),
                  children: [
                    {
                      index: true,
                      element: (
                        <Suspense fallback={<Loading />}>
                          <ProfileManagement tab={0} />
                        </Suspense>
                      ),
                    },
                    {
                      path: '/settings/profileManagement/wirelessNetwork/:name',
                      breadcrumb: ':name',
                      element: (
                        <Suspense fallback={<Loading />}>
                          <CreateEditWirelessNetwork />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  path: '/settings/profileManagement/radios',
                  breadcrumb: t('settings.profileManagement.radiosPageTitle'),
                  children: [
                    {
                      index: true,
                      element: (
                        <Suspense fallback={<Loading />}>
                          <ProfileManagement tab={1} />
                        </Suspense>
                      ),
                    },
                    {
                      path: '/settings/profileManagement/radios/:name',
                      breadcrumb: ':name',
                      element: (
                        <Suspense fallback={<Loading />}>
                          <CreateEditRadio />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  path: '/settings/profileManagement/virtualNetwork',
                  breadcrumb: t('settings.profileManagement.vlansPageTitle'),
                  children: [
                    {
                      index: true,
                      element: (
                        <Suspense fallback={<Loading />}>
                          <ProfileManagement tab={2} />
                        </Suspense>
                      ),
                    },
                    {
                      path: '/settings/profileManagement/virtualNetwork/:name',
                      breadcrumb: ':name',
                      element: (
                        <Suspense fallback={<Loading />}>
                          <CreateEditVirtualNetwork />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  path: '/settings/profileManagement/radius',
                  breadcrumb: t('settings.profileManagement.radiusPageTitle'),
                  children: [
                    {
                      index: true,
                      element: (
                        <Suspense fallback={<Loading />}>
                          <ProfileManagement tab={3} />
                        </Suspense>
                      ),
                    },
                    {
                      path: '/settings/profileManagement/radius/:name',
                      breadcrumb: ':name',
                      element: (
                        <Suspense fallback={<Loading />}>
                          <CreateEditRadius />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  path: '/settings/profileManagement/captivePortal',
                  breadcrumb: t('settings.profileManagement.captivePortalPageTitle'),
                  children: [
                    {
                      index: true,
                      element: (
                        <Suspense fallback={<Loading />}>
                          <ProfileManagement tab={4} />
                        </Suspense>
                      ),
                    },
                    {
                      path: '/settings/profileManagement/captivePortal/:name',
                      breadcrumb: ':name',
                      element: (
                        <Suspense fallback={<Loading />}>
                          <CreateEditCaptivePortal />
                        </Suspense>
                      ),
                    },
                  ],
                },
              ],
            },
            {
              path: '/settings/static-route-firewall',
              breadcrumb: t('settings.staticRouteFirewall.pageTitle'),
              element: <Blank />,
              children: [
                {
                  index: true,
                  element: (
                    <Suspense fallback={<Loading />}>{buildVersion === BuildVersionList.controller ? <StaticRouteFirewall /> : <NoMatch />}</Suspense>
                  ),
                },
                {
                  path: '/settings/static-route-firewall/static-route/:name',
                  breadcrumb: ':name',
                  element: (
                    <Suspense fallback={<Loading />}>
                      {buildVersion === BuildVersionList.controller ? <CreateEditStaticRoute /> : <NoMatch />}
                    </Suspense>
                  ),
                },
                {
                  path: '/settings/static-route-firewall/ipv6-static-route/:name',
                  breadcrumb: ':name',
                  element: (
                    <Suspense fallback={<Loading />}>
                      {buildVersion === BuildVersionList.controller ? <CreateEditStaticRoute /> : <NoMatch />}
                    </Suspense>
                  ),
                },
                {
                  path: '/settings/static-route-firewall/firewall/:name',
                  breadcrumb: ':name',
                  element: (
                    <Suspense fallback={<Loading />}>{buildVersion === BuildVersionList.controller ? <CreateEditFirewall /> : <NoMatch />}</Suspense>
                  ),
                },
                {
                  path: '/settings/static-route-firewall/ipv6-firewall/:name',
                  breadcrumb: ':name',
                  element: (
                    <Suspense fallback={<Loading />}>{buildVersion === BuildVersionList.controller ? <CreateEditFirewall /> : <NoMatch />}</Suspense>
                  ),
                },
                {
                  path: '/settings/static-route-firewall/port-forward/:name',
                  breadcrumb: ':name',
                  element: (
                    <Suspense fallback={<Loading />}>
                      {buildVersion === BuildVersionList.controller ? <CreateEditPortForward /> : <NoMatch />}
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: '/settings/redirect-landing',
              breadcrumb: t('sidenav.navsRedirectLanding'),
              element: (
                <Suspense fallback={<Loading />}>
                  <RedirectLanding />
                </Suspense>
              ),
            },
            {
              path: '/settings/system',
              breadcrumb: t('settings.system.pageTitle'),
              element: <Blank />,
              children: [
                {
                  index: true,
                  element: (
                    <Suspense fallback={<Loading />}>
                      <System />
                    </Suspense>
                  ),
                },
                {
                  path: '/settings/system/mail-servers-config',
                  breadcrumb: t('settings.system.mailServices.cardTitle'),
                  element: (
                    <Suspense fallback={<Loading />}>
                      <MailServicesConfig />
                    </Suspense>
                  ),
                },
                {
                  path: '/settings/system/system-log',
                  breadcrumb: t('settings.system.systemLog.systemLogs'),
                  element: (
                    <Suspense fallback={<Loading />}>
                      <SystemLog />
                    </Suspense>
                  ),
                },
              ],
            },
            //TODO:暫不支援firmwareUpdate，先註解掉
            // {
            //   path: '/settings/firmwareUpdate',
            //   breadcrumb: t('settings.firmwareUpdate.pageTitle'),
            //   element: (
            //     <Suspense fallback={<Loading />}>
            //       <FirmwareUpdate />
            //     </Suspense>
            //   ),
            // },
            // {
            //   path: '/settings/firmwareUpdate/ReleaseNote',
            //   breadcrumb: 'Release Note',
            //   element: (
            //     <Suspense fallback={<Loading />}>
            //       <ReleaseNote />
            //     </Suspense>
            //   ),
            // },
          ],
        },
        {
          path: '/profile',
          element: <Blank />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <Profile tab={0} />
                </Suspense>
              ),
            },
            {
              path: '/profile/myAccount',
              element: (
                <Suspense fallback={<Loading />}>
                  <Profile tab={0} />
                </Suspense>
              ),
            },
            {
              path: '/profile/members',
              element: <Suspense fallback={<Loading />}>{role === Role.Owner ? <Profile tab={1} /> : <NoMatch />}</Suspense>,
            },
          ],
        },
        {
          path: '/rtty/:serialNumber',
          element: (
            <Suspense fallback={<Loading />}>
              <Rtty />
            </Suspense>
          ),
        },
        { path: '*', element: <NoMatch /> },
      ],
    },
  ];

  const portalRoutes: RouteObjectWithBreadcrumbs[] = [
    {
      path: '/',
      element: (
        <Suspense fallback={<Loading />}>
          <RequireAuth>
            <PortalLayout />
          </RequireAuth>
        </Suspense>
      ),
      children: [
        { index: true, element: <PortalSite /> },
        {
          path: '/sites',
          element: <Blank />,
          children: [
            {
              index: true,
              element: (
                <Suspense fallback={<Loading />}>
                  <PortalSite />
                </Suspense>
              ),
            },
            {
              path: '/sites/:siteId',
              element: (
                <Suspense fallback={<Loading />}>
                  <Blank />
                </Suspense>
              ),
              children: addSiteIdPrefix(controllerRoute),
            },
          ],
        },
        {
          path: '/devices',
          element: <Blank />,
          children: [
            {
              index: true,
              element: <Suspense fallback={<Loading />}>{role === PortalRole.Root ? <PortalDevices tab={0} /> : <NoMatch />}</Suspense>,
            },
            {
              path: '/devices/rontoDevices',
              element: <Suspense fallback={<Loading />}>{role === PortalRole.Root ? <PortalDevices tab={0} /> : <NoMatch />}</Suspense>,
            },
            {
              path: '/devices/redirectLanding',
              element: <Suspense fallback={<Loading />}>{role === PortalRole.Root ? <PortalDevices tab={1} /> : <NoMatch />}</Suspense>,
            },
          ],
        },
        {
          path: '/members',
          element: <Suspense fallback={<Loading />}>{role === PortalRole.Root ? <PortalMember tab={0} /> : <NoMatch />}</Suspense>,
        },
        {
          path: '/settings',
          element: <Suspense fallback={<Loading />}>{role === PortalRole.Root ? <System /> : <NoMatch />}</Suspense>,
        },
        {
          path: '/profile',
          element: (
            <Suspense fallback={<Loading />}>
              <PortalProfile />
            </Suspense>
          ),
        },
        { path: '*', element: <NoMatch /> },
      ],
    },
    {
      path: '/register',
      element: (
        <Suspense fallback={<Loading />}>
          <PortalLogin />
        </Suspense>
      ),
    },
    {
      path: '/api/users/sendEmail',
      element: (
        <Suspense fallback={<Loading />}>
          <PortalLogin />
        </Suspense>
      ),
    },
    {
      path: 'portal.sonicfi-networks.com/reset_password',
      element: (
        <Suspense fallback={<Loading />}>
          <PortalLogin />
        </Suspense>
      ),
    },
  ];

  return buildVersion === BuildVersionList.portal ? portalRoutes : controllerRoute;
};

const App = () => {
  const { buildVersion } = useConfig();
  const location = useLocation();
  const regex = /^\/sites\/(\d+)(\/.*)?$/;
  const match = location.pathname.match(regex);
  let siteId: number | undefined;
  if (match) {
    siteId = +match[1];
    console.log(`siteId: ${siteId}`);
    setItem('siteId', siteId);
  } else {
    removeItem('siteId');
  }

  if (buildVersion === BuildVersionList.portal) {
    document.title = 'Cloud Portal';
  } else {
    document.title = 'Controller';
  }

  return (
    <LoadingMaskProvider>
      <ToastProvider>
        <AuthPortalProvider>
          <AuthProvider siteId={siteId}>
            <ModalProvider>{useRoutes(getRoutes(buildVersion, getItem('currentRole')))}</ModalProvider>
          </AuthProvider>
        </AuthPortalProvider>
      </ToastProvider>
    </LoadingMaskProvider>
  );
};

export default App;
